/* eslint-disable @typescript-eslint/no-explicit-any */
import { Col, DatePicker, Drawer, notification, Row, Select, Tag } from "antd";
import { useMemo, useState, useEffect } from "react";
import Styles from "./supervisorDashboard.module.scss";
import {
  usePrimaryLocations,
  useTotePriorities,
  useToteStatus,
  useTrolleyStatus,
} from "../common/queries";
import { convertLocalToUTC, convertUTCToLocal } from "../../utils/timeUtils";
import { useMutation } from "@tanstack/react-query";
import { getSecondaryLocations } from "./mutations";
import dayjs from "dayjs";

const DashboardFilters = ({ open, setOpen, payload, setPayload }: any) => {
  // api calls
  const { data: primaryLocations, isLoading: l1 } = usePrimaryLocations();
  const { data: toteStatusData } = useToteStatus();
  const { data: trolleyStatusData } = useTrolleyStatus();
  const { data: totePriorityData } = useTotePriorities();

  // global
  const dashboardView = localStorage?.getItem("dashboardTab");

  // State
  const [secondaryLocations, setSecondaryLocations] = useState<any>([]);

  // utility
  const handleCancel = () => setOpen(false);

  const tagStatuses = [
    // { label: "Overdue", value: "Overdue", key: "1" },
    { label: "Missed Scan", value: "missed", key: "2" },
    { label: "Delayed", value: "delayed", key: "3" },
  ];

  const toteStatusOptions = useMemo((): Array<{
    label: string;
    value: number;
  }> => {
    if (!toteStatusData?.tote_list?.length) {
      return [];
    }
    const filteredToteStatuses = toteStatusData.tote_list.filter(
      (status) => status.status_name.toLowerCase() !== "completed"
    );

    return filteredToteStatuses?.map((status) => ({
      label: status.status_name,
      value: status.id,
    }));
  }, [toteStatusData]);

  const trolleyStatusOptions = useMemo((): Array<{
    label: string;
    value: number;
  }> => {
    if (!trolleyStatusData?.trolley_list?.length) {
      return [];
    }
    return trolleyStatusData?.trolley_list?.map((status) => ({
      label: status.status_name,
      value: status.id,
    }));
  }, [trolleyStatusData]);

  const locationOptions = useMemo((): Array<{
    label: string;
    value: number;
  }> => {
    if (!primaryLocations?.length) {
      return [];
    }

    return primaryLocations.map((location) => ({
      label: location.location_name,
      value: location.id,
    }));
  }, [primaryLocations]);

  const priorityOptions = useMemo(() => {
    return (
      totePriorityData?.map((priority) => ({
        label: priority.priority,
        value: priority?.id,
      })) || []
    );
  }, [totePriorityData]);

  const getSecondaryLocationsMutate = useMutation({
    mutationFn: (payload: any) => getSecondaryLocations(payload),
    onSuccess: (data) => {
      const temp: any = data?.secondary_locations?.map((i: any) => ({
        label: `${i?.secondary_name} - ${i?.barcode}`,
        value: i?.id,
        key: i?.id,
      }));
      setSecondaryLocations(temp);
    },
    onError: (response) => {
      notification?.error({ message: response?.message });
    },
  });

  // Fetch secondary locations when drawer opens and primary_location exists
  useEffect(() => {
    if (
      open &&
      payload?.primary_location &&
      payload?.primary_location?.length > 0
    ) {
      getSecondaryLocationsMutate?.mutate({
        primary_location_ids: payload.primary_location,
      });
    } else if (
      open &&
      (!payload?.primary_location || payload?.primary_location?.length === 0)
    ) {
      // Clear secondary locations if no primary location is selected
      setSecondaryLocations([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload?.primary_location]);

  return (
    <Drawer
      open={open}
      width={"33%"}
      onClose={handleCancel}
      closable={false}
      headerStyle={{ display: "none" }}
    >
      <Row justify="space-between" align="middle">
        <h2>
          Filter {dashboardView === "Flow" ? "Flow View" : "Location View"}
        </h2>
        <div
          className={Styles.resetButton}
          onClick={() => {
            setPayload({
              ...payload,
              start_date: null,
              end_date: null,
              use_start_date: null,
              use_end_date: null,
              delivery_start_date: null,
              delivery_end_date: null,
              primary_location: null,
              secondary_location: null,
              tote_status: null,
              trolley_status: null,
              priority_status: null,
              missed: null,
              delayed: null,
            });
            // setOpen(false);
          }}
        >
          Reset
        </div>
      </Row>
      <Row gutter={[10, 30]} style={{ marginTop: "10%" }}>
        <Col span={24}>
          <div className={Styles.labels}>Primary Location</div>
          <Select
            className={Styles.filterFields}
            options={locationOptions.map((option) => ({
              label: option.label,
              value: option.value,
            }))}
            value={payload?.primary_location}
            loading={l1}
            maxTagCount={1}
            mode="multiple"
            allowClear
            showSearch
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            onChange={(val) => {
              setPayload({ ...payload, primary_location: val });
              if (val && val.length > 0) {
                getSecondaryLocationsMutate?.mutate({
                  primary_location_ids: val,
                });
              } else {
                setSecondaryLocations([]);
                setPayload({
                  ...payload,
                  primary_location: null,
                  secondary_location: null,
                });
              }
            }}
          />
        </Col>
        <Col span={24}>
          <div className={Styles.labels}>Secondary Location</div>
          <Select
            className={Styles.filterFields}
            options={secondaryLocations}
            value={payload?.secondary_location}
            loading={l1}
            maxTagCount={1}
            mode="multiple"
            allowClear
            showSearch
            placeholder={
              payload?.primary_location?.length > 0
                ? "Select secondary location"
                : "Select primary location first"
            }
            disabled={
              !payload?.primary_location ||
              payload?.primary_location?.length === 0
            }
            filterOption={(input, option: any) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            onChange={(val) =>
              setPayload({ ...payload, secondary_location: val })
            }
          />
        </Col>
        <Col span={24}>
          <Row gutter={[10, 0]}>
            <Col span={12}>
              <div className={Styles.labels}>Registered From Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    start_date: value ? convertLocalToUTC(value) : null,
                  });
                }}
                value={
                  payload?.start_date
                    ? dayjs(convertUTCToLocal(payload?.start_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.end_date
                    ? current?.isAfter(payload?.end_date, "day")
                    : false
                }
                placement="bottomLeft"
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
            <Col span={12}>
              <div className={Styles.labels}>Registered To Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    end_date: value ? convertLocalToUTC(value) : null,
                  });
                }}
                value={
                  payload?.end_date
                    ? dayjs(convertUTCToLocal(payload?.end_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.start_date
                    ? current?.isBefore(payload?.start_date, "day")
                    : false
                }
                placement="bottomLeft"
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[10, 0]}>
            <Col span={12}>
              <div className={Styles.labels}>Date of Delivery From Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    delivery_start_date: value
                      ? convertLocalToUTC(value)
                      : null,
                  });
                }}
                value={
                  payload?.delivery_start_date
                    ? dayjs(convertUTCToLocal(payload?.delivery_start_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.delivery_end_date
                    ? current?.isAfter(payload?.delivery_end_date, "day")
                    : false
                }
                placement="bottomLeft"
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
            <Col span={12}>
              <div className={Styles.labels}>Date of Delivery To Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    delivery_end_date: value ? convertLocalToUTC(value) : null,
                  });
                }}
                value={
                  payload?.delivery_end_date
                    ? dayjs(convertUTCToLocal(payload?.delivery_end_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.delivery_start_date
                    ? current?.isBefore(payload?.delivery_start_date, "day")
                    : false
                }
                placement="topLeft"
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement?.parentElement as HTMLElement) ||
                  document.body
                }
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <Row gutter={[10, 0]}>
            <Col span={12}>
              <div className={Styles.labels}>Date of Use From Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    use_start_date: value ? convertLocalToUTC(value) : null,
                  });
                }}
                value={
                  payload?.use_start_date
                    ? dayjs(convertUTCToLocal(payload?.use_start_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.use_end_date
                    ? current?.isAfter(payload?.use_end_date, "day")
                    : false
                }
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                placement="bottomLeft"
                allowClear
                style={{ width: "100%" }}
              />
            </Col>

            <Col span={12}>
              <div className={Styles.labels}>Date of Use To Date</div>
              <DatePicker
                showTime={{ format: "HH:mm" }}
                onChange={(value) => {
                  setPayload({
                    ...payload,
                    use_end_date: value ? convertLocalToUTC(value) : null,
                  });
                }}
                value={
                  payload?.use_end_date
                    ? dayjs(convertUTCToLocal(payload?.use_end_date))
                    : null
                }
                className={Styles.filterFields}
                format="DD MMM YYYY, HH:mm"
                disabledDate={(current) =>
                  payload?.use_start_date
                    ? current?.isBefore(payload?.use_start_date, "day")
                    : false
                }
                placement="bottomLeft"
                getPopupContainer={(triggerNode) =>
                  (triggerNode?.parentElement as HTMLElement) || document.body
                }
                allowClear
                style={{ width: "100%" }}
              />
            </Col>
          </Row>
        </Col>

        <Col span={24}>
          <div className={Styles.labels}>Priority</div>
          <Row gutter={[0, 10]}>
            {priorityOptions.map((option) => {
              const isChecked = payload?.priority_status?.includes(
                option.value
              );

              return (
                <Col key={option.value}>
                  <Tag.CheckableTag
                    checked={isChecked}
                    className={`chip ${isChecked ? "active" : ""}`}
                    onChange={(checked) => {
                      if (checked) {
                        // Single-select: only this option in array
                        setPayload({
                          ...payload,
                          priority_status: [option.value],
                        });
                      } else {
                        // Uncheck: clear to null
                        setPayload({
                          ...payload,
                          priority_status: null,
                        });
                      }
                    }}
                  >
                    {option.label}
                  </Tag.CheckableTag>
                </Col>
              );
            })}
          </Row>
        </Col>
        <Col span={24}>
          <div className={Styles.labels}>Trolley Status</div>
          <Row gutter={[0, 10]}>
            {trolleyStatusOptions.map((option) => {
              const isChecked = payload?.trolley_status?.includes(option.value);

              return (
                <Col key={option.value}>
                  <Tag.CheckableTag
                    checked={isChecked}
                    className={`chip ${isChecked ? "active" : ""}`}
                    onChange={(checked) => {
                      const updated = checked
                        ? [...(payload?.trolley_status || []), option.value]
                        : payload.trolley_status.filter(
                            (item: any) => item !== option.value
                          );

                      setPayload({
                        ...payload,
                        trolley_status: updated?.length > 0 ? updated : null,
                      });
                    }}
                  >
                    {option.label}
                  </Tag.CheckableTag>
                </Col>
              );
            })}
          </Row>
        </Col>

        <Col span={24}>
          <div className={Styles.labels}>Tote Status</div>
          <Row gutter={[0, 10]}>
            {toteStatusOptions.map((option) => {
              const isChecked = payload?.tote_status?.includes(option.value);

              return (
                <Col key={option.value}>
                  <Tag.CheckableTag
                    checked={isChecked}
                    className={`chip ${isChecked ? "active" : ""}`}
                    onChange={(checked) => {
                      const updated = checked
                        ? [...(payload?.tote_status || []), option.value]
                        : payload.tote_status.filter(
                            (item: any) => item !== option.value
                          );

                      setPayload({
                        ...payload,
                        tote_status: updated?.length > 0 ? updated : null,
                      });
                    }}
                  >
                    {option.label}
                  </Tag.CheckableTag>
                </Col>
              );
            })}
          </Row>
        </Col>

        <Col span={24}>
          <div className={Styles.labels}>Tags</div>

          <Row gutter={[0, 10]}>
            {tagStatuses?.map((option) => {
              const isChecked = payload?.[option?.value]?.[0];

              return (
                <Col key={option.value}>
                  <Tag.CheckableTag
                    checked={isChecked}
                    className={`chip ${isChecked ? "active" : ""}`}
                    onChange={(checked) => {
                      setPayload((prev: any) => ({
                        ...prev,
                        [option.value]: checked ? [true] : null,
                      }));
                    }}
                  >
                    {option.label}
                  </Tag.CheckableTag>
                </Col>
              );
            })}
          </Row>
        </Col>
      </Row>
    </Drawer>
  );
};

export default DashboardFilters;
